<template>
  <div class="courses-layout">
    <div
      :class="{'courses-layout__wrapper--not-found': notFound}"
      class="courses-layout__wrapper"
    >
      <main class="courses-layout__main">
        <header class="courses-layout__top">
          <div class="courses-layout__top-container">
            <UiPicture
              v-if="pageTitleIcon && isDesktop"
              :data="pageTitleIcon"
              :width="48"
              :height="48"
              :is-lazy="false"
              :alt="`Иконка направления «${pageTitle}»`"
              class="courses-layout__title-icon"
            />

            <h1
              class="courses-layout__title h"
              :class="isMobile ? 'h--2' : 'h--3'"
            >
              {{ pageTitle }}
            </h1>
          </div>
        </header>

        <div
          v-if="isDesktop"
          class="courses-layout__sidebar"
        >
          <UiNavigation
            v-element-visibility="(isVisibility) => isVisibility && sendCatalogShowMenu()"
            :sections="directionsWithQuery"
            :current-section-index="activeDirection?.index"
            :active-course-index="activeSection?.index"
            @click:section="(index) => {
              handleSectionClick(index)
              sendCatalogClickMenu('body', {
                selected_direction_id: directionsWithQuery[index]?.slug || '',
              })
            }"
            @click:courses="(index) => sendCatalogClickMenu('body', {
              selected_topics_id: activeDirection?.items?.[index]?.slug || '',
            })"
          />
        </div>

        <div class="courses-layout__content">
          <div class="courses-layout__filters">
            <UiFilterButtons
              v-if="isMobile"
              :counter="selectedFilterCount"
              :sorting-title="selectedSort"
              :toggle-value="isSubscriptionAvailable ? 'pro' : undefined"
              :toggle-text="isSubscriptionAvailable ? 'Pro' : undefined"
              toggle-bg-color="var(--accent-subscription-color)"
              :is-subscribe-available="isSubscriptionAvailable"
              class="courses-layout__mobile-filter-buttons"
              @click:sorting="openSortingPopup = !openSortingPopup"
              @click:filters="openFilterPopup = !openFilterPopup"
            />

            <CoursesFilterDesktop
              v-if="isDesktop"
              class="courses-layout__desktop-filter"
            />

            <client-only>
              <UiChosenFilters
                v-if="chosenList.length > 0"
                :list="chosenList"
                :show-more="true"
                :is-mobile="isMobile"
                class="courses-layout__chosen-filters"
                @chip:remove="handleChipRemove"
                @click:clear="handleChipsClear"
              />
            </client-only>
          </div>

          <template v-if="!notFound">
            <section class="course-page">
              <CardsList
                :cards="courses || []"
                :active-subscription="activeSubscription"
                :user-has-subscription="Boolean(userSubsription)"
                class="course-page__cards"
                @click:card="(event) => sendCatalogClickCard({
                  page_number: pagination?.page || 1,
                  shelf_number: Math.ceil(event.cardNumber / countCardsInShelf),
                  card_number: (event.cardNumber - 1) % countCardsInShelf + 1,
                  nomenclature_id: event.nomenclatureId,
                  nomenclature_type: event.nomenclatureType,
                })"
                @visible:card="(event) => sendCatalogShowCard({
                  page_number: pagination?.page || 1,
                  shelf_number: Math.ceil(event.cardNumber / countCardsInShelf),
                  card_number: (event.cardNumber - 1) % countCardsInShelf + 1,
                  nomenclature_id: event.nomenclatureId,
                  nomenclature_type: event.nomenclatureType,
                })"
              />

              <UiPagination
                v-if="pagination && pagination.lastPage !== 1"
                :pagination="pagination"
                :url="router.currentRoute.value.fullPath"
                class="courses-page__pagination"
                @click="handlePageClick"
              />
            </section>
          </template>

          <UiFilterNotFound
            v-else
            class="courses-layout__not-found"
          >
            Попробуйте изменить запрос или&nbsp;посмотрите наши рекомендации
          </UiFilterNotFound>
        </div>
      </main>

      <UiSortingPopupFilter
        v-if="openSortingPopup"
        :sorting="filters?.sorting || []"
        :current-sorting="(sortingQueries.sorting as string)"
        @click:submit="handleSortClick"
        @close="openSortingPopup = false"
      />

      <CoursesFilterMobile
        v-if="openFilterPopup"
        @click:close="openFilterPopup = !openFilterPopup"
      />
    </div>

    <UiPreloader
      v-if="isLoading"
      class="courses-layout__preloader"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { vElementVisibility } from '@vueuse/components'

  import { getQueryFromRoute } from '@academica-box/helpers/filters'
  import type { FilterItem } from '@academica-box/components/Filter/types'
  import UiSortingPopupFilter from '@academica-box/components/Filter/mobile/UiSortingPopupFilter/UiSortingPopupFilter.vue'
  import UiFilterNotFound from '@academica-box/components/Filter/UiFilterNotFound/UiFilterNotFound.vue'
  import UiNavigation from '@academica-box/components/Blocks/UiNavigation/UiNavigation.vue'
  import UiChosenFilters from '@academica-box/components/Filter/UiChosenFilters/UiChosenFilters.vue'
  import UiFilterButtons from '@academica-box/components/Filter/mobile/UiFilterButtons/UiFilterButtons.vue'
  import UiPreloader from '@academica-box/components/Loaders/UiPreloader/UiPreloader.vue'
  import UiPagination from '@academica-box/components/Blocks/UiPagination/UiPagination.vue'
  import UiPicture from '@academica-box/components/Media/UiPicture/UiPicture.vue'
  import { useDirectionsStore } from '@academica-box/composables/store/directions'
  import { useSubscribesStore } from '@academica-box/composables/store/subscribes'
  import { useUserStore } from '@academica-box/composables/store/user'
  import { setRouteQueryParam, setRouteQueryParams, useRouteQueryParams } from '@academica-box/composables/useRouteQueryParam'
  import type { FiltersApiGetCoursesRequest, FiltersApiGetFiltersRequest } from '@academica-box/swagger/academgen'

  import { FilterKeys } from '@academica-box/types'
  import type { ChosenFilter } from '@/types'

  import CoursesFilterDesktop from '@/components/Filters/CoursesFilterDesktop.vue'
  import CoursesFilterMobile from '@/components/Filters/CoursesFilterMobile.vue'
  import CardsList from '@/components/Courses/CardsList.vue'

  import { useCoursesStore } from '@/store/courses'
  import { useFiltersStore } from '@/store/filters'
  import {
    sendCatalogShowCard,
    sendCatalogClickCard,
    sendCatalogShowMenu,
    sendCatalogClickMenu,
  } from '@/pages/catalog/analytics'

  // Реактивные переменны
  const route = useRoute()
  const router = useRouter()

  const directionsStore = useDirectionsStore()
  const coursesStore = useCoursesStore()
  const filtersStore = useFiltersStore()
  const { directionsWithQuery, activeDirection, activeSection } = storeToRefs(directionsStore)
  const { courses, isLoading, pagination } = storeToRefs(coursesStore)
  const { filters, flattenCompanies } = storeToRefs(filtersStore)
  const subscribesStore = useSubscribesStore()
  const { userSubsription, activeSubscription, isSubscriptionAvailable } = storeToRefs(subscribesStore)
  const userStore = useUserStore()

  const viewport = useViewport()

  const openFilterPopup = ref(false)
  const openSortingPopup = ref(false)

  const countCardsInShelf = computed(() => {
    if (viewport.matches('xl', 'lg', 'sm', 'xs')) {
      return 2
    } else {
      return 3
    }
  })

  const filterQueries = useRouteQueryParams({
    [FilterKeys.companies]: 'array',
    [FilterKeys.levels]: 'array',
    [FilterKeys.duration_min]: 'string',
    [FilterKeys.duration_max]: 'string',
    [FilterKeys.languages]: 'array',
    [FilterKeys.page]: 'string',
  })

  const sortingQueries = useRouteQueryParams({
    [FilterKeys.sorting]: 'string',
  })

  const searchQuery = useRouteQueryParams({
    [FilterKeys.search]: 'string',
  })

  const unsubscribeUserStore = userStore.$onAction(({
    name,
    after,
  }) => {
    if (name === 'login' || name === 'logout') {
      after(() => {
        getCatalogData()
      })
    }
  })

  watch([filterQueries, sortingQueries, searchQuery], async (newValue, oldValue) => {
    if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
      const isDifferentPages = newValue[0].page !== oldValue[0].page

      await getCatalogData()

      if (isDifferentPages) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  })

  onServerPrefetch(() => {
    directionsStore.setActiveDirection(route)
    directionsStore.setActiveSection(route)
  })

  watch(courses, () => {
    directionsStore.setActiveDirection(route)
    directionsStore.setActiveSection(route)
  })

  onMounted(() => {
    const isMoreSlovGame = useCookie('more_slov')

    if ('more_slov' in route.query) {
      isMoreSlovGame.value = 'true'
    }
  })

  onUnmounted(() => {
    unsubscribeUserStore()
  })

  const getCatalogData = () => Promise.all([
    coursesStore.getCourses({
      ...getQueryFromRoute(route),
      limit: 12,
    } as FiltersApiGetCoursesRequest),
    filtersStore.getFilters(getQueryFromRoute(route) as FiltersApiGetFiltersRequest),
  ])

  await useAsyncData(
    'getCatalogData',
    () => getCatalogData()
  )

  // Компьютед
  const seoTitle = computed(() => {
    if (activeSection.value?.title && activeSection.value.title !== 'Все') {
      return `Бесплатные курсы в категории «${activeSection.value.title}» | Академика`
    } else if (activeDirection.value?.title && activeDirection.value.title !== 'Все курсы и программы') {
      return `Бесплатные курсы в категории «${activeDirection.value?.title}» | Академика`
    }

    return 'Бесплатные курсы | Академика'
  })

  const seoDescription = computed(() => {
    if (activeDirection.value?.title[0] === 'Все курсы и программы' || !activeSection.value?.title) {
      return 'Найдите для себя бесплатный онлайн-курс на платформе Академика и начните учиться уже сегодня'
    }

    if (activeSection.value?.title && activeSection.value.title !== 'Все') {
      return `Найдите для себя бесплатные онлайн-курс по направлению «${activeSection.value?.title}» на платформе Академика и начните учиться уже сегодня.`
    } else if (activeDirection.value?.title && activeDirection.value.title !== 'Все курсы и программы') {
      return `Найдите для себя бесплатные онлайн-курс по направлению «${activeDirection.value?.title}» на платформе Академика и начните учиться уже сегодня.`
    }

    return 'Получите навыки по востребованным направлениям: программирование, разработка, дизайн, бизнес, менеджмент, экономика. Узнавайте новое и сразу применяйте знания на практике.'
  })

  const selectedFilterCount = computed(() => {
    const selectedFilters = { ...filterQueries.value }

    if (selectedFilters[FilterKeys.companies] && chosenCompanies.value.length === 0) {
      delete selectedFilters[FilterKeys.companies]
    }

    if (selectedFilters[FilterKeys.duration_min] && selectedFilters[FilterKeys.duration_max]) {
      delete selectedFilters[FilterKeys.duration_max]
    }

    if (selectedFilters[FilterKeys.page]) {
      delete selectedFilters[FilterKeys.page]
    }

    return Object.values(selectedFilters).length
  })

  const selectedSort = computed(() => {
    return filters.value?.sorting.find(item => item.value === sortingQueries.value.sorting)?.text || 'По умолчанию'
  })

  const chosenCompanies = computed(() => {
    return (filterQueries.value[FilterKeys.companies] as string[] || [])?.map((item) => {
      const findedItem = flattenCompanies.value.find(company => company.value === item)

      if (!findedItem) {
        return null
      }

      return {
        ...findedItem,
        query: FilterKeys.companies,
      } as FilterItem
    }).filter(Boolean)
  })

  const chosenList = computed(() => {
    const levels = (filterQueries.value[FilterKeys.levels] as string[] || []).map((item) => {
      return {
        ...filters.value?.[FilterKeys.levels].find(type => type.value === item),
        query: FilterKeys.levels,
      } as FilterItem
    })

    const languages = (filterQueries.value[FilterKeys.languages] as string[] || []).map((item) => {
      return {
        ...filters.value?.[FilterKeys.languages].find(type => type.value === item),
        query: FilterKeys.languages,
      } as FilterItem
    })

    return [...chosenCompanies.value, ...levels, ...languages].filter(Boolean) as FilterItem[]
  })

  const notFound = computed(() => {
    return courses.value?.length === 0
  })

  const isMobile = computed(() => viewport.isLessThan('lg'))
  const isDesktop = computed(() => viewport.isGreaterThan('md'))

  const pageTitle = computed(() => {
    if (notFound.value && searchQuery.value[FilterKeys.search]) {
      return `Ничего не нашли по запросу «${searchQuery.value[FilterKeys.search]}»`
    } else if (searchQuery.value[FilterKeys.search]) {
      return `Результат по запросу «${searchQuery.value[FilterKeys.search]}: бесплатные курсы» (${pagination.value?.totalCourses})`
    } else if (activeSection.value?.title && activeSection.value.title !== 'Все') {
      return `${activeSection.value.title}: бесплатные курсы`
    } else {
      return `${activeDirection.value?.title}: бесплатные курсы`
    }
  })

  const pageTitleIcon = computed(() => {
    if (notFound.value || searchQuery.value[FilterKeys.search]) {
      return undefined
    } else {
      return activeDirection.value?.images
    }
  })

  // Методы
  const handleSortClick = (value: string) => {
    setRouteQueryParam(sortingQueries, FilterKeys.sorting, value)
    openSortingPopup.value = false
  }

  const handleSectionClick = (index: number) => {
    router.push(directionsWithQuery.value[index]?.link ?? '')
  }

  const handleChipRemove = (item: ChosenFilter) => {
    const res = JSON.parse(JSON.stringify(filterQueries.value))

    if (Array.isArray(res[item.query])) {
      const index = (res[item.query] as string[]).findIndex(value => value === item.value)

      if (index > -1) {
        (res[item.query] as string[]).splice(index, 1)
      }
    } else {
      res[item.query] = ''
    }

    setRouteQueryParams(filterQueries, {
      ...res,
      [FilterKeys.page]: '',
    })
  }

  const handleChipsClear = () => {
    setRouteQueryParams(filterQueries, {
      [FilterKeys.companies]: [],
      [FilterKeys.levels]: [],
      [FilterKeys.duration_min]: '',
      [FilterKeys.duration_max]: '',
      [FilterKeys.languages]: [],
      [FilterKeys.page]: '',
    })
  }

  const handlePageClick = (value: number) => {
    setRouteQueryParam(filterQueries, FilterKeys.page, value.toString())
  }

  useSeoMeta({
    title: seoTitle.value,
    description: seoDescription.value,
    ogTitle: seoTitle.value,
    ogDescription: seoDescription.value,
  })

  watch([seoTitle, seoDescription], () => {
    useSeoMeta({
      title: seoTitle.value,
      description: seoDescription.value,
      ogTitle: seoTitle.value,
      ogDescription: seoDescription.value,
    })
  })

  useSchemaOrg([
    defineListItem({
      itemListElement: courses.value?.map((course) => {
        return {
          item: {
            id: course.link,
            type: 'Course',
            name: course?.title || '',
            description: `Курс - ${course.title}`,
            provider: {
              type: 'Organization',
              name: course.provider.text,
            },
            offers: [{
              category: course.isFree ? 'Free' : course.isSubscribeAvailable ? 'Subscription' : 'Paid',
              priceCurrency: 'RUB',
              price: course.price.current
            }],
            hasCourseInstance: [{
              courseMode: 'Online',
              courseWorkload: course.duration?.value ? `PT${parseInt(course.duration.value)}H` : '',
            }],
            aggregateRating: course.rating?.value,
            inLanguage: 'ru',
          }
        }
      })
    })
  ])
</script>

<style lang="scss">
  @use '@academica-box/styles/UiContainer/variables' as *;
  @use '@academica-box/styles/UiContainer/mixin' as *;
  @use '@academica-box/utils/styles/mixins/UiMedia' as *;
  @use '@academica-box/utils/styles/mixins/UiZIndex' as *;

  .courses-layout__wrapper {
    @include z-index(base);

    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 100vw;
    min-height: 100vh;

    &::before {
      @include z-index(below);

      position: absolute;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100%;
      background-color: var(--bg-secondary-color);

      @include media(md-max) {
        content: '';
      }
    }

    &--not-found::before {
      content: none;
    }
  }

  .courses-layout__main {
    display: flex;
    flex-direction: column;
    grid-row: 1/-1;

    @include media(lg) {
      @include ui-container(lg);

      display: grid;
      grid-gap: 48px 32px;
      grid-template-rows: auto 1fr;
      grid-template-columns: 288px 1fr;
      padding-bottom: 96px;
    }

    @include media(xl) {
      grid-template-columns: 320px 1fr;
    }
  }

  .courses-layout__top {
    margin-bottom: 6px;
    padding-bottom: 20px;
    border-radius: 0 0 20px 20px;
    background-color: var(--bg-main-color);

    @include media(lg) {
      display: flex;
      grid-row: 1/2;
      grid-column: 1/-1;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }

  .courses-layout__top-container {
    @include media(sm-max) {
      margin-right: 20px;
      margin-left: 20px;
    }

    @include media(md, md-max) {
      margin-right: 32px;
      margin-left: 32px;
    }

    @include media(lg) {
      display: flex;
      align-items: center;
    }
  }

  .courses-layout__title-icon {
    flex-shrink: 0;
    align-self: flex-start;
    width: 48px;
    height: 48px;
    margin-right: 12px;
  }

  .courses-layout__title.h {
    margin-bottom: 2px;

    @include media(md) {
      margin-top: 4px;
    }

    @include media(lg) {
      margin: 3px 0;
    }
  }

  .courses-layout__tags-nav {
    margin: 20px -20px 0;

    @include media(md) {
      margin-right: calc((100vw - #{$ui-md-c}) / -2);
      margin-left: calc((100vw - #{$ui-md-c}) / -2);
    }

    @include media(lg) {
      display: none;
    }
  }

  .courses-layout__sidebar {
    @include media(md-max) {
      display: none;
    }

    @include media(lg) {
      grid-row: 2/-1;
      grid-column: 1/2;
    }
  }

  .courses-layout__content {
    display: flex;
    flex-direction: column;
    padding: 20px 20px 56px;

    @include media(md-max) {
      height: 100%;
      border-radius: 20px 20px 0 0;
      background-color: var(--bg-main-color);
    }

    @include media(md) {
      padding-right: 36px;
      padding-left: 36px;
    }

    @include media(lg) {
      padding: 0;
    }
  }

  .courses-layout__filters {
    margin-bottom: 20px;

    @include media(lg) {
      margin-bottom: 32px;
    }
  }

  .courses-layout__mobile-filter-buttons {
    @include media(lg) {
      display: none;
    }
  }

  .courses-filter-desktop.courses-layout__desktop-filter {
    @include media(md-max) {
      display: none;
    }
  }

  .courses-layout__chosen-filters {
    display: grid;
    grid-template-columns: auto min-content;
    margin-top: 20px;

    @include media(lg) {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid var(--stroke-main-color);
    }
  }

  .courses-layout__not-found {
    @include media(md-max) {
      margin: auto;
    }
  }

  .courses-page__pagination {
    justify-content: center;
    margin-top: 56px;

    @include media(lg) {
      margin-top: 96px;
    }
  }
</style>
